import {
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputBase,
  Paper,
  Slider,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  ClearRounded,
  CloseRounded,
  FilterListRounded,
  PhotoCameraRounded,
  SearchRounded,
  SignalCellularAltRounded
} from '@material-ui/icons';
import {
  Autocomplete,
  Pagination,
  ToggleButton,
  ToggleButtonGroup
} from '@material-ui/lab';
import { DatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import clsx from 'clsx';
import domtoimage from 'dom-to-image';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { CountBy } from '../../api/projects';
import { RootState } from '../../app/store';
import ChartCountDocument from '../../components/ChartCountDocument';
import ChartDocRevisionStatus, {
  Status
} from '../../components/ChartDocRevisionStatus';
import ChartOverallProgress from '../../components/ChartOverallProgress';
import DocRevDashboardCard from '../../components/DocRevDashboardCard';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import { Discipline } from '../MasterData/Discipline/disciplineSlice';
import { Purpose } from '../MasterData/Purpose/purposeSlice';
import {
  getCountDocumentRevisionsByCompletedDateAsync,
  getCountDocumentRevisionsByIssueDateAsync,
  getDocumentRevisionStatusesAsync,
  getDocumentRevisionsAsync,
  getProjectAsync,
  getSumProjectProgressesAsync
} from '../Project/projectSlice';

interface Mark {
  value: number;
  label: number | string;
}

enum KeyChip {
  DATE = 'date',
  DISCIPLINE = 'discipline',
  PURPOSE = 'purpose'
}

interface FilterChip {
  key: KeyChip;
  label: string;
  value: moment.Moment | Discipline | Purpose;
}

export default function ProjectDashboard() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const {
    selectedProject,
    documentRevisionStatuses,
    documentRevisions,
    countDocumentRevisionByIssueDate,
    countDocumentRevisionByCompletedDate,
    sumProjectProgress
  } = useSelector((state: RootState) => state.projects);

  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );
  const { data: purposes } = useSelector((state: RootState) => state.purposes);

  const params = useParams<{ projectId: string }>();
  const projectId = Number.parseInt(params.projectId);

  const [selectedDisciplines, setSelectedDisciplines] = useState<Discipline[]>(
    []
  );
  const [selectedPurposes, setSelectedPurposes] = useState<Purpose[]>([]);

  const [yearRange, setYearRange] = useState<number>(1);

  const TODAY: moment.Moment = moment().startOf('day');

  const [dateRange, setDateRange] = useState<{
    startDate: number;
    endDate: number;
  }>({
    startDate: findMinMaxDateNumberFormat(TODAY, yearRange).min,
    endDate: findMinMaxDateNumberFormat(TODAY, yearRange).max
  });

  const startDate = moment(dateRange.startDate);
  const endDate = moment(dateRange.endDate);

  const [canGetChartData, setCanGetChartData] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<{
    startDate: moment.Moment;
    endDate: moment.Moment;
  }>({ startDate: moment(), endDate: moment() });
  const [appliedFilter, setAppliedFilter] = useState<FilterChip[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<Status | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const limit = 20;

  const [openFilter, setOpenFilter] = useState<boolean>(false);

  // For external users of the SMILE Project
  const isShowExternalReport = selectedProject?.projectNo === 'B-24-1047';

  // For external users of the Energy Prime Company Project
  const isShowEnergyPrimeReport =
    selectedProject?.projectNo === 'B-25-3101' ||
    selectedProject?.projectNo === 'B-25-3114';

  const renderStatuses = [
    {
      id: 1,
      name: 'waitForIssue',
      label: t('projectDashboard.statuses.notIssue') as string,
      quantity: documentRevisionStatuses
        ? documentRevisionStatuses.waitForIssue.count
        : 0,
      chart: {
        labels: [t('projectDashboard.chart.onTime'), t('projectDashboard.chart.overdue')],
        data: [
          documentRevisionStatuses
            ? documentRevisionStatuses.waitForIssue.onTime
            : 0,
          documentRevisionStatuses
            ? documentRevisionStatuses.waitForIssue.overdue
            : 0
        ]
      }
    },
    {
      id: 2,
      name: 'inProgress',
      label: t('projectDashboard.statuses.reviewing') as string,
      quantity: documentRevisionStatuses
        ? documentRevisionStatuses.inProgress.count
        : 0,
      chart: {
        labels: [t('projectDashboard.chart.onTime'), t('projectDashboard.chart.overdue')],
        data: [
          documentRevisionStatuses
            ? documentRevisionStatuses.inProgress.onTime
            : 0,
          documentRevisionStatuses
            ? documentRevisionStatuses.inProgress.overdue
            : 0
        ]
      }
    },
    {
      id: 3,
      name: 'done',
      label: t('projectDashboard.statuses.finished') as string,
      quantity: documentRevisionStatuses
        ? documentRevisionStatuses.done.count
        : 0,
      chart: {
        labels: [
          t('projectDashboard.chart.completed'),
          t('projectDashboard.chart.rejected'),
          t('projectDashboard.chart.canceled'),
          t('projectDashboard.chart.completedDelay')
        ],
        data: [
          documentRevisionStatuses
            ? documentRevisionStatuses.done.completed
            : 0,
          documentRevisionStatuses ? documentRevisionStatuses.done.rejected : 0,
          documentRevisionStatuses ? documentRevisionStatuses.done.canceled : 0,
          documentRevisionStatuses
            ? documentRevisionStatuses.done.completedDelayIssue
            : 0
        ]
      }
    }
  ];

  const drawer = (
    <div role="presentation">
      <div className={classes.drawerHeader}>
        <Typography variant="h6">{t('dashboard.filter.title')}</Typography>
        <Tooltip title={t('common.close') as string}>
          <IconButton onClick={handleFilterDrawer(false)}>
            <CloseRounded />
          </IconButton>
        </Tooltip>
      </div>
      <Divider />
      <div className={classes.filterContainer}>
        <Autocomplete
          className={classes.filterField}
          id="discipline"
          options={disciplines}
          multiple
          getOptionLabel={(option) => option?.name}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={`${option?.name}`}
                {...getTagProps({ index })}
                variant="outlined"
                size="small"
                deleteIcon={<ClearRounded />}
              />
            ))
          }
          value={selectedDisciplines}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={(event, newValue) => {
            setSelectedDisciplines(newValue);
            setCanGetChartData(true);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={
                selectedDisciplines.length === 0 ? (t('dashboard.filter.filterByDiscipline') as string) : ''
              }
            />
          )}
        />
        <Autocomplete
          className={classes.filterField}
          id="purpose"
          options={purposes}
          multiple
          getOptionLabel={(option) => option?.abbreviation}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={`${option?.abbreviation}`}
                {...getTagProps({ index })}
                variant="outlined"
                size="small"
                deleteIcon={<ClearRounded />}
              />
            ))
          }
          value={selectedPurposes}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={(event, newValue) => {
            setSelectedPurposes(newValue);
            setCanGetChartData(true);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={
                selectedPurposes.length === 0 ? (t('dashboard.filter.filterByPurpose') as string) : ''
              }
            />
          )}
        />
        <div className={classes.toggleButtonField}>
          <span>
            <Typography variant="body2" color="textSecondary">
              {t('dashboard.filter.selectRange')}
            </Typography>
          </span>
          <ToggleButtonGroup
            size="small"
            value={yearRange}
            exclusive
            onChange={handleChartLength}
          >
            {[1 / 12, 1 / 4, 1 / 2, 1, 3, 5].map((value) => {
              let label;
              if (value < 1) {
                label = `±${value * 12}m`;
              } else {
                label = `±${value}y`;
              }
              return (
                <ToggleButton
                  className={classes.toggleButton}
                  key={value}
                  value={value}
                >
                  <Typography variant="caption">{label}</Typography>
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </div>
        <div className={classes.flexDate}>
          <DatePicker
            className={classes.dateField}
            autoOk
            size="small"
            variant="inline"
            id="start-date"
            label={t('dashboard.filter.from')}
            format="MMM d, yyyy"
            value={startDate}
            maxDate={new Date(moment(endDate).toLocaleString())}
            onChange={(newValue) => {
              if (newValue) handleDatePickerChange(newValue, 'startDate');
            }}
            required
          />
          <DatePicker
            className={classes.dateField}
            autoOk
            size="small"
            variant="inline"
            id="end-date"
            label={t('dashboard.filter.to')}
            format="MMM d, yyyy"
            value={endDate}
            minDate={new Date(moment(startDate).toLocaleString())}
            onChange={(newValue) => {
              if (newValue) handleDatePickerChange(newValue, 'endDate');
            }}
            required
          />
        </div>
        <div className={classes.slider}>
          <Slider
            value={[
              Math.max(
                dateRange.startDate,
                findMinMaxDateNumberFormat(TODAY, yearRange).min
              ),
              Math.min(
                dateRange.endDate,
                findMinMaxDateNumberFormat(TODAY, yearRange).max
              )
            ]}
            min={findMinMaxDateNumberFormat(TODAY, yearRange).min}
            max={findMinMaxDateNumberFormat(TODAY, yearRange).max}
            onChange={handleDateChange}
            step={86400000} // 1 day = 86,400,000 milliseconds
            marks={generateMarks({
              yearRange,
              sliderValue: findMinMaxDateNumberFormat(TODAY, yearRange)
            })}
          />
        </div>
        <div className={classes.filterButton}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={resetChartData}
          >
            {t('common.clear')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={!canGetChartData}
            onClick={() =>
              requestChartAndDocumentRevisionData({ startDate, endDate })
            }
          >
            {t('common.apply')}
          </Button>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    dispatch(getProjectAsync(projectId));
    setSelectedDate({ startDate, endDate });
    getChartData({
      projectId,
      disciplines: selectedDisciplines,
      purposes: selectedPurposes,
      startDate,
      endDate,
      search
    });
    getDocRevisionData({
      projectId,
      disciplines: selectedDisciplines,
      purposes: selectedPurposes,
      startDate,
      endDate,
      status: selectedStatus,
      search,
      page,
      limit
    });
    getCountDocumentRevision({
      projectId,
      disciplines: selectedDisciplines,
      purposes: selectedPurposes,
      startDate,
      endDate
    });
    getSumProjectProgress({
      projectId,
      disciplines: selectedDisciplines,
      purposes: selectedPurposes
    });
  }, []);

  //To get chart data for displaying
  async function getChartData({
    projectId,
    disciplines,
    purposes,
    startDate,
    endDate,
    search
  }: {
    projectId: number;
    disciplines: Discipline[];
    purposes: Purpose[];
    startDate: moment.Moment;
    endDate: moment.Moment;
    search: string | null;
  }) {
    const params = {
      projectId,
      disciplineIds: disciplines.map((discipline) => discipline.id),
      purposeIds: purposes.map((purpose) => purpose.id),
      startDate: new Date(startDate.toLocaleString()),
      endDate: new Date(endDate.toLocaleString()),
      search,
      today: new Date(moment().toLocaleString())
    };
    dispatch(getDocumentRevisionStatusesAsync(params));
  }

  //To get project document revisions
  async function getDocRevisionData({
    projectId,
    disciplines,
    purposes,
    startDate,
    endDate,
    status,
    search,
    page,
    limit
  }: {
    projectId: number;
    disciplines: Discipline[];
    purposes: Purpose[];
    startDate: moment.Moment;
    endDate: moment.Moment;
    status: Status | null;
    search: string | null;
    page: number;
    limit: number;
  }) {
    const params = {
      projectId,
      disciplineIds: disciplines.map((discipline) => discipline.id),
      purposeIds: purposes.map((purpose) => purpose.id),
      startDate: new Date(startDate.toLocaleString()),
      endDate: new Date(endDate.toLocaleString()),
      status: status ? status.name : null,
      search,
      page,
      limit
    };
    dispatch(getDocumentRevisionsAsync(params));
  }

  //To get chart data for displaying
  async function getCountDocumentRevision({
    projectId,
    disciplines,
    purposes,
    startDate,
    endDate
  }: {
    projectId: number;
    disciplines: Discipline[];
    purposes: Purpose[];
    startDate: moment.Moment;
    endDate: moment.Moment;
  }) {
    const params = {
      projectId,
      disciplineIds: disciplines.map((discipline) => discipline.id),
      purposeIds: purposes.map((purpose) => purpose.id),
      startDate: new Date(startDate.toLocaleString()),
      endDate: new Date(endDate.toLocaleString())
    };
    dispatch(
      getCountDocumentRevisionsByIssueDateAsync({
        ...params,
        countBy: CountBy.issuedDate
      })
    );
    dispatch(
      getCountDocumentRevisionsByCompletedDateAsync({
        ...params,
        countBy: CountBy.completedDate
      })
    );
  }

  //To get chart data for displaying
  async function getSumProjectProgress({
    projectId,
    disciplines,
    purposes
  }: {
    projectId: number;
    disciplines: Discipline[];
    purposes: Purpose[];
  }) {
    const params = {
      projectId,
      disciplineIds: disciplines.map((discipline) => discipline.id),
      purposeIds: purposes.map((purpose) => purpose.id)
    };
    dispatch(getSumProjectProgressesAsync(params));
  }

  //To get all information both chart and project document revisions
  function requestChartAndDocumentRevisionData({
    startDate,
    endDate
  }: {
    startDate: moment.Moment;
    endDate: moment.Moment;
  }) {
    const newStartDate = startDate.startOf('day');
    const newEndDate = endDate.endOf('day');

    function getAppliedFilter(): FilterChip[] {
      const disciplineChips: FilterChip[] = selectedDisciplines.map(
        (discipline) => {
          return {
            key: KeyChip.DISCIPLINE,
            label: discipline.name,
            value: discipline
          };
        }
      );
      const purposeChips: FilterChip[] = selectedPurposes.map((purpose) => {
        return {
          key: KeyChip.PURPOSE,
          label: purpose.abbreviation,
          value: purpose
        };
      });
      return [...disciplineChips, ...purposeChips];
    }

    setAppliedFilter(getAppliedFilter());
    setPage(0);
    setSelectedDate({ startDate: newStartDate, endDate: newEndDate });
    getChartData({
      projectId,
      disciplines: selectedDisciplines,
      purposes: selectedPurposes,
      startDate: newStartDate,
      endDate: newEndDate,
      search
    });
    getDocRevisionData({
      projectId,
      disciplines: selectedDisciplines,
      purposes: selectedPurposes,
      startDate: newStartDate,
      endDate: newEndDate,
      status: selectedStatus,
      search,
      page: 0,
      limit
    });
    getCountDocumentRevision({
      projectId,
      disciplines: selectedDisciplines,
      purposes: selectedPurposes,
      startDate: newStartDate,
      endDate: newEndDate
    });
    getSumProjectProgress({
      projectId,
      disciplines: selectedDisciplines,
      purposes: selectedPurposes
    });
    setOpenFilter(false);
    setCanGetChartData(false);
  }

  //To reset all information both chart and project document revisions
  function resetChartData() {
    const YEAR_RANGE = 1;
    const startDate = moment(findMinMaxDateNumberFormat(TODAY, YEAR_RANGE).min);
    const endDate = moment(findMinMaxDateNumberFormat(TODAY, YEAR_RANGE).max);

    setSelectedDisciplines([]);
    setSelectedPurposes([]);
    setDateRange({
      startDate: convertDateToNumber(startDate),
      endDate: convertDateToNumber(endDate)
    });
    setYearRange(YEAR_RANGE);
    setSelectedDate({ startDate, endDate });
    setAppliedFilter([]);
    setSelectedStatus(null);
    setSearch(null);
    setPage(0);
    getChartData({
      projectId,
      disciplines: [],
      purposes: [],
      startDate,
      endDate,
      search: null
    });
    getDocRevisionData({
      projectId,
      disciplines: [],
      purposes: [],
      startDate,
      endDate,
      status: null,
      search: null,
      page: 0,
      limit
    });
    getCountDocumentRevision({
      projectId,
      disciplines: [],
      purposes: [],
      startDate,
      endDate
    });
    getSumProjectProgress({
      projectId,
      disciplines: [],
      purposes: []
    });
    setOpenFilter(false);
    setCanGetChartData(false);
  }

  function convertDateToNumber(date: moment.Moment): number {
    return Number.parseInt(moment(date).format('x'), 10);
  }

  function findMinMaxDateNumberFormat(
    date: moment.Moment,
    yearRange: number
  ): { min: number; max: number } {
    const min = convertDateToNumber(
      moment(date).startOf('day').subtract(yearRange, 'years')
    );
    const max = convertDateToNumber(
      moment(date).endOf('day').add(yearRange, 'years')
    );
    return { min, max };
  }

  function generateMarks({
    yearRange,
    sliderValue
  }: {
    yearRange: number;
    sliderValue: { min: number; max: number };
  }): Mark[] {
    const positions: Mark[] = [];
    const today = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    );
    const thisYear = today.getFullYear();
    const thisMonth = today.getMonth();

    if (yearRange >= 1) {
      // find mark for first date of this year
      const firstDateOfThisYear = new Date(thisYear, 0, 1).getTime();

      let positionOfPastYear = firstDateOfThisYear;
      let pastYear = thisYear;
      while (true) {
        if (positionOfPastYear >= sliderValue.min) {
          positions.push({
            value: positionOfPastYear,
            label:
              yearRange < 5 ? pastYear : `'${pastYear.toString().substring(2)}`
          });
          positionOfPastYear = convertDateToNumber(
            moment(positionOfPastYear).subtract(1, 'years')
          );
          pastYear--;
        } else {
          break;
        }
      }
      let positionOfFutureYear = convertDateToNumber(
        moment(firstDateOfThisYear).add(1, 'years')
      );
      let futureYear = thisYear + 1;
      while (true) {
        if (positionOfFutureYear <= sliderValue.max) {
          positions.push({
            value: positionOfFutureYear,
            label:
              yearRange < 5
                ? futureYear
                : `'${futureYear.toString().substring(2)}`
          });
          positionOfFutureYear = convertDateToNumber(
            moment(positionOfFutureYear).add(1, 'years')
          );
          futureYear++;
        } else {
          break;
        }
      }
    } else {
      const gapMonth = Math.max(1, Math.round(4 * yearRange));
      // find mark for first date of this month
      const firstDateOfThisMonth = new Date(thisYear, thisMonth, 1).getTime();

      let positionOfPastMonth = firstDateOfThisMonth;
      while (true) {
        if (positionOfPastMonth >= sliderValue.min) {
          positions.push({
            value: positionOfPastMonth,
            label: moment(positionOfPastMonth).format('MMM')
          });
          positionOfPastMonth = convertDateToNumber(
            moment(positionOfPastMonth).subtract(gapMonth, 'months')
          );
        } else {
          break;
        }
      }
      let positionOfFutureMonth = convertDateToNumber(
        moment(firstDateOfThisMonth).add(gapMonth, 'months')
      );
      while (true) {
        if (positionOfFutureMonth <= sliderValue.max) {
          positions.push({
            value: positionOfFutureMonth,
            label: moment(positionOfFutureMonth).format('MMM')
          });
          positionOfFutureMonth = convertDateToNumber(
            moment(positionOfFutureMonth).add(gapMonth, 'months')
          );
        } else {
          break;
        }
      }
    }

    return positions;
  }

  function handleDateChange(event: any, newValue: number | number[]) {
    const newDate = newValue as number[];
    setDateRange({ startDate: newDate[0], endDate: newDate[1] });
    setCanGetChartData(true);
  }

  function handleDatePickerChange(
    date: MaterialUiPickersDate,
    field: 'startDate' | 'endDate'
  ) {
    const dateNumberFormat = convertDateToNumber(moment(date));
    const newDateRange = { ...dateRange, [field]: dateNumberFormat };

    setDateRange(newDateRange);
    setCanGetChartData(true);
  }

  function handleChartLength(
    event: React.MouseEvent<HTMLElement>,
    newChartLength: number | null
  ) {
    if (newChartLength) {
      setDateRange({
        startDate: convertDateToNumber(
          moment(TODAY).subtract(newChartLength, 'years')
        ),
        endDate: convertDateToNumber(moment(TODAY).add(newChartLength, 'years'))
      });
      setYearRange(newChartLength);
      setCanGetChartData(true);
    }
  }

  function handleFilterDrawer(open: boolean) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setOpenFilter(open);
    };
  }

  //To load new project document revisions whet status was changed
  function loadDocumentRevisionData(status: Status | null) {
    setPage(0);
    getDocRevisionData({
      projectId,
      disciplines: selectedDisciplines,
      purposes: selectedPurposes,
      startDate,
      endDate,
      status,
      search,
      page: 0,
      limit
    });
  }

  //Change search and load new project document revisions when clear search field
  function handleChangeSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const keyword = event.target.value;
    setSearch(keyword === '' ? null : keyword);
    if (!keyword) {
      setPage(0);
      getChartData({
        projectId,
        disciplines: selectedDisciplines,
        purposes: selectedPurposes,
        startDate,
        endDate,
        search: null
      });
      getDocRevisionData({
        projectId,
        disciplines: selectedDisciplines,
        purposes: selectedPurposes,
        startDate,
        endDate,
        status: selectedStatus,
        search: null,
        page: 0,
        limit
      });
    }
  }

  //Load new project document revisions when search has it's value
  function handleSearch() {
    if (search) {
      setPage(0);
      getChartData({
        projectId,
        disciplines: selectedDisciplines,
        purposes: selectedPurposes,
        startDate,
        endDate,
        search
      });
      getDocRevisionData({
        projectId,
        disciplines: selectedDisciplines,
        purposes: selectedPurposes,
        startDate,
        endDate,
        status: selectedStatus,
        search,
        page: 0,
        limit
      });
    }
  }

  //Load new project document revisions when page was changed
  function handleChangePage(event: React.ChangeEvent<unknown>, value: number) {
    setPage(value - 1); //The first value of pagination is 1, not 0
    getDocRevisionData({
      projectId,
      disciplines: selectedDisciplines,
      purposes: selectedPurposes,
      startDate,
      endDate,
      status: selectedStatus,
      search,
      page: value - 1,
      limit
    });
  }

  async function downloadPngByElementId(elementId: string): Promise<void> {
    const scale = 3;
    const element = document.getElementById(elementId);
    try {
      if (element) {
        const dataUrl = await domtoimage.toPng(element, {
          height: element.offsetHeight * scale,
          width: element.offsetWidth * scale,
          bgcolor: '#f0f2f5',
          style: {
            transform: `scale(${scale})`,
            transformOrigin: 'top left'
          }
        });
        const link = document.createElement('a');
        link.download = `screenshots-${selectedProject?.name}-${moment()
          .format('YYYYMMDD_HHmmss')
          .toString()}.png`;
        link.href = dataUrl;
        link.click();
        dispatch(loadingSlice.actions.success({ message: t('messages.success.saved') as string }));
      } else {
        throw new Error(`${elementId} not found`);
      }
    } catch (error) {
      console.error(error);
      dispatch(loadingSlice.actions.error({ message: t('messages.error.somethingWentWrong') as string }));
    }
  }

  function filterChips(): FilterChip[] {
    const chips: FilterChip[] = [
      {
        key: KeyChip.DATE,
        label: `${t('dashboard.filter.from')}: ${moment(selectedDate.startDate).format('ll')}`,
        value: selectedDate.startDate
      },
      {
        key: KeyChip.DATE,
        label: `${t('dashboard.filter.to')}: ${moment(selectedDate.endDate).format('ll')}`,
        value: selectedDate.endDate
      },
      ...appliedFilter
    ];
    return chips;
  }

  function handleDeleteChip(chipToDelete: FilterChip) {
    return function () {
      const newStartDate = startDate.startOf('day');
      const newEndDate = endDate.endOf('day');

      let newDisciplines = selectedDisciplines;
      let newPurposes = selectedPurposes;
      if (chipToDelete.key === KeyChip.DISCIPLINE) {
        newDisciplines = selectedDisciplines.filter(
          (discipline) => discipline.name !== chipToDelete.label
        );
        setSelectedDisciplines(newDisciplines);
      }
      if (chipToDelete.key === KeyChip.PURPOSE) {
        newPurposes = selectedPurposes.filter(
          (purpose) => purpose.abbreviation !== chipToDelete.label
        );
        setSelectedPurposes(newPurposes);
      }
      const newAppliedFilter = appliedFilter.filter(
        (filter) => filter.label !== chipToDelete.label
      );
      setAppliedFilter(newAppliedFilter);
      setPage(0);
      getChartData({
        projectId,
        disciplines: newDisciplines,
        purposes: newPurposes,
        startDate: newStartDate,
        endDate: newEndDate,
        search
      });
      getDocRevisionData({
        projectId,
        disciplines: newDisciplines,
        purposes: newPurposes,
        startDate: newStartDate,
        endDate: newEndDate,
        status: selectedStatus,
        search,
        page: 0,
        limit
      });
      getCountDocumentRevision({
        projectId,
        disciplines: newDisciplines,
        purposes: newPurposes,
        startDate: newStartDate,
        endDate: newEndDate
      });
      getSumProjectProgress({
        projectId,
        disciplines: newDisciplines,
        purposes: newPurposes
      });
    };
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="projectDashboard"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id
        }}
      />
      <div className={classes.titleBox}>
        <Typography component="p" variant="h4">
          {t('dashboard.projectDashboard')}
          <Tooltip title={t('dashboard.screenshots') as string} arrow>
            <IconButton
              className={classes.marginIconButton}
              size="small"
              onClick={() => downloadPngByElementId('container')}
            >
              <PhotoCameraRounded />
            </IconButton>
          </Tooltip>
        </Typography>
        <div className={classes.buttonDocumentLists}>
          <Button
            className={clsx({ [classes.defaultButton]: isShowExternalReport })}
            variant="contained"
            color={isShowExternalReport ? 'default' : 'primary'}
            size="small"
            startIcon={<SignalCellularAltRounded />}
            onClick={() => {
              if (isShowEnergyPrimeReport) {
                window.open(
                  `${process.env.REACT_APP_ENERGY_PRIME_COMPANY_DOCUMENT_LIST_REPORT_WEB_URL}`,
                  '_blank'
                );
              } else {
                window.open(
                  `${process.env.REACT_APP_DOCUMENT_LIST_REPORT_WEB_URL}?filter=DocumentListwithMH/ProjectNo eq '${selectedProject?.projectNo}'`,
                  '_blank'
                );
              }
            }}
          >
            {t('dashboard.viewDocumentLists')}
          </Button>
          {isShowExternalReport && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SignalCellularAltRounded />}
              onClick={() => {
                window.open(
                  process.env.REACT_APP_EXTERNAL_DOCUMENT_LIST_REPORT_WEB_URL,
                  '_blank'
                );
              }}
            >
              {t('dashboard.externalUserDashboard')}
            </Button>
          )}
        </div>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.filterDashboardContainer}>
            <div className={classes.flexGrow}>
              {filterChips().map((chip, index) => {
                return (
                  <Chip
                    key={index}
                    className={classes.filterChip}
                    size="small"
                    variant="outlined"
                    label={chip.label}
                    onClick={
                      chip.key === KeyChip.DATE
                        ? handleFilterDrawer(true)
                        : undefined
                    }
                    onDelete={
                      chip.key === KeyChip.DATE
                        ? undefined
                        : handleDeleteChip(chip)
                    }
                    deleteIcon={<ClearRounded />}
                  />
                );
              })}
            </div>
            <Tooltip title={t('common.filter') as string} arrow>
              <IconButton
                size="small"
                edge="end"
                onClick={handleFilterDrawer(true)}
              >
                <FilterListRounded />
              </IconButton>
            </Tooltip>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.card}>
            <Typography variant="subtitle1" gutterBottom>
              {t('dashboard.projectProgressCurve')}
            </Typography>
            <Divider className={classes.divider} />
            <ChartOverallProgress
              renderSumProjectProgress={sumProjectProgress}
            />
          </Paper>
          <Paper className={classes.card}>
            <Typography variant="subtitle1">
              {t('dashboard.numberOfDocumentRevisions')}
            </Typography>
            <div className={classes.groupByText}>
              <Typography variant="caption">
                {t('dashboard.inViewOf')}
                <Chip
                  className={classes.chip}
                  variant="outlined"
                  size="small"
                  label={t('dashboard.issueDate')}
                />
              </Typography>
            </div>
            <Divider className={classes.divider} />
            <ChartCountDocument
              startDate={selectedDate.startDate}
              endDate={selectedDate.endDate}
              documentRevisionCounts={countDocumentRevisionByIssueDate}
            />
          </Paper>
          <Paper className={clsx(classes.card, classes.removeMarginInSm)}>
            <Typography variant="subtitle1">
              {t('dashboard.numberOfDocumentRevisions')}
            </Typography>
            <div className={classes.groupByText}>
              <Typography variant="caption">
                {t('dashboard.inViewOf')}
                <Chip
                  className={classes.chip}
                  variant="outlined"
                  size="small"
                  label={t('dashboard.completedDate')}
                />
              </Typography>
            </div>
            <Divider className={classes.divider} />
            <ChartCountDocument
              startDate={selectedDate.startDate}
              endDate={selectedDate.endDate}
              documentRevisionCounts={countDocumentRevisionByCompletedDate}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper className={classes.card}>
            <div className={classes.flexAlignCenter}>
              <Typography
                className={clsx(classes.flexGrow, classes.lineHeight120)}
                variant="subtitle1"
                gutterBottom
              >
                {t('dashboard.documentRevisions')}
              </Typography>
              <Paper className={classes.paperSearch} variant="outlined">
                <SearchRounded className={classes.colorTextSecondary} />
                <InputBase
                  placeholder={t('common.search') as string}
                  className={classes.searchInput}
                  type="search"
                  spellCheck={false}
                  fullWidth
                  value={search === null ? '' : search}
                  onChange={handleChangeSearch}
                  onKeyPress={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  inputProps={{ 'aria-label': 'search' }}
                />
              </Paper>
            </div>
            <Divider className={classes.divider} />
            <ChartDocRevisionStatus
              renderStatuses={renderStatuses}
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              loadDocumentRevisionData={loadDocumentRevisionData}
            />
            <Typography
              className={classes.marginTop}
              variant="body2"
              color="textSecondary"
            >
              {t('common.about')} {documentRevisions.count} {documentRevisions.count > 1 ? t('common.results') : t('common.result')}
            </Typography>
            {documentRevisions.count > 0 ? (
              <DocRevDashboardCard
                renderDocumentRevisions={documentRevisions.data}
              />
            ) : (
              <div className={classes.fileNotFound}>{t('dashboard.documentNotFound')}</div>
            )}
            <div className={classes.pagination}>
              <Pagination
                count={Math.ceil(documentRevisions.count / limit)}
                shape="rounded"
                page={page + 1}
                onChange={handleChangePage}
              />
            </div>
          </Paper>
        </Grid>
      </Grid>
      <Drawer
        className={classes.drawerDesktop}
        anchor="right"
        open={openFilter}
        onClose={handleFilterDrawer(false)}
      >
        {drawer}
      </Drawer>
      <Drawer
        className={classes.drawerMobile}
        anchor="bottom"
        open={openFilter}
        onClose={handleFilterDrawer(false)}
      >
        {drawer}
      </Drawer>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    titleBox: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        display: 'block',
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    buttonDocumentLists: {
      display: 'flex',
      gap: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        marginTop: theme.spacing(1)
      },
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'center'
      }
    },
    defaultButton: {
      backgroundColor:
        theme.palette.type === 'dark'
          ? theme.palette.text.primary
          : theme.palette.background.paper
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    removeMarginInSm: {
      [theme.breakpoints.down('sm')]: {
        marginBottom: 0
      }
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    filterDashboardContainer: {
      padding: theme.spacing(1, 2, 1, 1),
      borderRadius: theme.spacing(2),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    marginIconButton: {
      margin: theme.spacing(-1, 0, -1, 1)
    },
    toggleButtonLayout: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingBottom: theme.spacing(1),
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    toggleButtonField: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      '& > *': {
        marginLeft: theme.spacing(1),
        '&:first-child': {
          marginLeft: theme.spacing(0)
        }
      }
    },
    filterField: {
      width: '100%',
      marginBottom: theme.spacing(1)
    },
    toggleButton: {
      width: 36
    },
    flexDate: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    dateField: {
      width: 120
    },
    slider: {
      padding: theme.spacing(0, 2, 2, 2)
    },
    paperSearch: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 2, 1, 1),
      borderRadius: 18,
      width: 240,
      height: 36,
      zIndex: 2
    },
    searchInput: {
      marginLeft: theme.spacing(1)
    },
    colorTextSecondary: {
      color: theme.palette.text.secondary
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexGrow: {
      flexGrow: 1
    },
    lineHeight120: {
      lineHeight: '120%'
    },
    filterContainer: {
      padding: theme.spacing(1, 2, 2, 2),
      '& > *': {
        marginTop: theme.spacing(1)
      }
    },
    filterChip: {
      margin: theme.spacing(0.5)
    },
    filterButton: {
      '& > *': {
        marginTop: theme.spacing(1)
      }
    },
    drawerDesktop: {
      display: 'block',
      [theme.breakpoints.down('xs')]: {
        display: 'none'
      },
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: 400,
        borderRadius: theme.spacing(2, 0, 0, 2)
      }
    },
    drawerMobile: {
      display: 'none',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      },
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: '100%',
        borderRadius: theme.spacing(2, 2, 0, 0)
      }
    },
    drawerHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0, 1, 0, 3),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar
    },
    fileNotFound: {
      padding: theme.spacing(4, 2),
      textAlign: 'center'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    chip: {
      marginLeft: theme.spacing(0.5),
      paddingTop: theme.spacing(0.25),
      fontWeight: 'normal',
      fontSize: 11,
      height: 18
    },
    marginTop: {
      marginTop: theme.spacing(1)
    },
    groupByText: {
      marginTop: theme.spacing(-0.75)
    }
  })
);
